import { EntityLocationDTO, RecordAtLocation } from '@user/store/map/typings'
import { isRecordEntityType } from '@user/store/map/constants'
import {
  ActiveMapFilters,
  isRecordHiddenByFilters,
} from '@user/features/MapView/RecordsHiddenByFilters/recordsHiddenByFilters'
import { multiRecordPinId } from '@user/utils/mapPinId'

/**
 * A click on a location that already has record(s) must open THEIR preview bubble, not the
 * address / add-lead bubble — otherwise the user can add a second record without ever seeing the
 * existing one.
 *
 * The decision is made from the `recordsAtLocation` lookup the click flow already performs, because
 * that is the only reliable source: the loaded map pins are filtered client-side (a record with a
 * knock status is dropped when no knock-status filter is selected) and can be missing from the
 * loaded tiles, and they don't reliably carry address fields — multi-record pins arrive with
 * `street1` / `city` / `postalCode` null.
 *
 * When a matching pin IS loaded it is preferred, since it carries the backend's own data (knock
 * status badge, address). Otherwise a pin is synthesised from the lookup, mirroring how the backend
 * shapes pins so the existing bubbles render unchanged.
 *
 * When at least one record here is still visible, a hidden record opens its bubble too — deliberate
 * (SH-1298), so a hidden record can't be silently duplicated behind a visible one. SH-1762 narrows
 * that: when a filter context is supplied and EVERY record at the location is hidden, this returns
 * null so the address bubble's "records hidden by your filters" banner shows instead (the banner is
 * itself the anti-duplicate guard — it warns the user and offers to reveal the hidden records).
 */

export type ClickedLocation = {
  latitude: number
  longitude: number
  formattedAddress?: string
}

/** A record row with the identifiers needed to open a bubble. */
type IdentifiedRecord = RecordAtLocation & {
  entityId: number
  contactLocationId: number
}

const sameType = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? '').toLowerCase() === (b ?? '').toLowerCase()

const identityKey = (record: IdentifiedRecord) =>
  `${record.entityType.toLowerCase()}|${record.entityId}|${record.serviceLocationId ?? ''}`

/**
 * Record rows worth routing to, deduplicated by (entityType, entityId, serviceLocationId).
 *
 * Rows without identifiers are dropped, which also means an older backend that doesn't send them
 * yields no candidates — the caller then falls back to the address bubble, i.e. today's behaviour.
 * The dedupe matters because a single record can own more than one ContactLocation row.
 */
export function pickRoutableRecords(
  records: RecordAtLocation[] | null | undefined,
): IdentifiedRecord[] {
  if (!records?.length) return []

  const seen = new Set<string>()
  const routable: IdentifiedRecord[] = []

  for (const record of records) {
    if (!isRecordEntityType(record.entityType)) continue
    if (!record.entityId || !record.contactLocationId) continue

    const identified = record as IdentifiedRecord
    const key = identityKey(identified)
    if (seen.has(key)) continue

    seen.add(key)
    routable.push(identified)
  }

  return routable
}

/** The active map filters plus current user, used to decide which records are hidden (SH-1762). */
export type FilterVisibilityContext = {
  filters: ActiveMapFilters
  currentUserId: string | null
}

/**
 * The pin whose preview bubble should open for this click, or null to keep the address bubble.
 *
 * When `filterContext` is supplied and every routable record is hidden by the active filters, this
 * returns null so the address bubble (and its hidden-by-filters banner) shows instead (SH-1762).
 * Omitting `filterContext` keeps the original behaviour of always routing to a record bubble.
 */
export function resolveExistingRecordPin(
  records: RecordAtLocation[] | null | undefined,
  loadedPins: EntityLocationDTO[],
  location: ClickedLocation,
  filterContext?: FilterVisibilityContext,
): EntityLocationDTO | null {
  const routable = pickRoutableRecords(records)
  if (routable.length === 0) return null

  const loadedPin =
    routable.length === 1
      ? findLoadedSinglePin(routable[0], loadedPins)
      : findLoadedClusterPin(routable, loadedPins)

  // A loaded pin means the record is actually rendered on the map right now — e.g. after a search
  // jump (overrideFilters returns every type regardless of the filters) or with the tile cache
  // disabled. Route to it so a visible record is never hidden behind the banner; this keeps
  // SH-1298's guard against adding a duplicate over an existing record. The filter-hidden fall-through
  // below only applies when nothing is loaded here.
  if (loadedPin) return loadedPin

  // SH-1762 — no pin is loaded at this location. If a filter context is supplied and EVERY routable
  // record is hidden by the active filters, fall through to the address bubble and its
  // hidden-by-filters banner; if any record would still be visible we route to it as before.
  if (
    filterContext &&
    routable.every((record) =>
      isRecordHiddenByFilters(
        record,
        filterContext.filters,
        filterContext.currentUserId,
      ),
    )
  ) {
    return null
  }

  return routable.length === 1
    ? buildSinglePin(routable[0], location)
    : buildClusterPin(routable, location)
}

function findLoadedSinglePin(
  record: IdentifiedRecord,
  loadedPins: EntityLocationDTO[],
): EntityLocationDTO | null {
  return (
    loadedPins.find(
      (pin) =>
        !pin.multitenant &&
        pin.entityId === record.entityId &&
        sameType(pin.entityType, record.entityType) &&
        (!pin.serviceLocationId ||
          !record.serviceLocationId ||
          pin.serviceLocationId === record.serviceLocationId),
    ) ?? null
  )
}

function findLoadedClusterPin(
  records: IdentifiedRecord[],
  loadedPins: EntityLocationDTO[],
): EntityLocationDTO | null {
  return (
    loadedPins.find(
      (pin) =>
        pin.multitenant &&
        records.every((record) =>
          pin.entities?.some(
            (member) =>
              member.entityId === record.entityId &&
              sameType(member.entityType, record.entityType),
          ),
        ),
    ) ?? null
  )
}

function averageCoordinate(
  records: IdentifiedRecord[],
  fallback: ClickedLocation,
): { latitude: number; longitude: number } {
  const located = records.filter(
    (record) => record.latitude != null && record.longitude != null,
  )
  if (located.length === 0) {
    return { latitude: fallback.latitude, longitude: fallback.longitude }
  }

  return {
    latitude:
      located.reduce((sum, record) => sum + (record.latitude ?? 0), 0) /
      located.length,
    longitude:
      located.reduce((sum, record) => sum + (record.longitude ?? 0), 0) /
      located.length,
  }
}

function buildSinglePin(
  record: IdentifiedRecord,
  location: ClickedLocation,
): EntityLocationDTO {
  return {
    // Mirrors the backend's singleton pin id (entity id concatenated with service location id).
    pinId: `${record.entityId}${record.serviceLocationId ?? ''}`,
    count: 1,
    entityId: record.entityId,
    entityType: record.entityType,
    serviceLocationId: record.serviceLocationId ?? 0,
    contactLocationId: record.contactLocationId,
    latitude: record.latitude ?? location.latitude,
    longitude: record.longitude ?? location.longitude,
    multitenant: false,
    entities: [],
    formattedAddress: location.formattedAddress,
  }
}

function buildClusterPin(
  records: IdentifiedRecord[],
  location: ClickedLocation,
): EntityLocationDTO {
  const ordered = [...records].sort((a, b) => a.entityId - b.entityId)
  const distinctTypes = new Set(
    ordered.map((record) => record.entityType.toLowerCase()),
  )

  const centre = averageCoordinate(ordered, location)

  return {
    // Mirrors the backend's cluster pin id and its "Mixed" label for groups holding more than one
    // record type.
    pinId: multiRecordPinId(centre.latitude, centre.longitude),
    count: ordered.length,
    entityId: ordered[0].entityId,
    entityType: distinctTypes.size === 1 ? ordered[0].entityType : 'Mixed',
    serviceLocationId: 0,
    latitude: centre.latitude,
    longitude: centre.longitude,
    multitenant: true,
    entities: ordered.map((record) => ({
      entityId: record.entityId,
      entityType: record.entityType,
      serviceLocationId: record.serviceLocationId ?? 0,
      contactLocationId: record.contactLocationId,
    })),
    formattedAddress: location.formattedAddress,
  }
}
